import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ArrowUpDown, Search, SlidersHorizontal, X } from "lucide-react";
import { CATEGORIES } from "../../lib/utils-cynos";
import CategoryChips from "../../components/product/CategoryChips";
import FilterSidebar from "../../components/product/FilterSidebar";
import ProductGrid from "../../components/product/ProductGrid";
import {
  DEFAULT_COLOR_FILTERS,
  collectProductColors,
  listProducts,
} from "../../services/productService";
import { setPageMeta } from "../../utils/seo";

const SORT_OPTIONS = [
  { value: "newest", label: "Newest" },
  { value: "best_selling", label: "Best Selling" },
  { value: "price_asc", label: "Price: Low to High" },
  { value: "price_desc", label: "Price: High to Low" },
];

const ProductListingPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const category = searchParams.get("category") || "all";
  const color = searchParams.get("color") || "";
  const search = searchParams.get("search") || "";
  const sortParam = searchParams.get("sort") || "newest";
  const [products, setProducts] = useState([]);
  const [colorOptions, setColorOptions] = useState(DEFAULT_COLOR_FILTERS);
  const [size, setSize] = useState("");
  const [sort, setSort] = useState(sortParam);
  const [maxPrice, setMaxPrice] = useState(2000);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [sortOpen, setSortOpen] = useState(false);

  useEffect(() => {
    setSort(sortParam);
  }, [sortParam]);

  useEffect(() => {
    setLoading(true);
    setError("");
    listProducts({ category, size, color, sort, maxPrice, search })
      .then(setProducts)
      .catch((err) => {
        console.warn("Product list failed", err);
        setProducts([]);
        setError("Could not load products. Please check that the backend and MongoDB are running.");
      })
      .finally(() => setLoading(false));
  }, [category, size, color, sort, maxPrice, search]);

  useEffect(() => {
    listProducts({ category })
      .then((data) => setColorOptions(collectProductColors(data)))
      .catch(() => setColorOptions(DEFAULT_COLOR_FILTERS));
  }, [category]);

  useEffect(() => {
    setPageMeta({
      title: "Cynos | Premium Oversized T-Shirts & Streetwear India",
      description: "Shop premium oversized T-shirts, anime tees, graphic tees, and streetwear styles for Indian youth at Cynos.",
      canonical: `${window.location.origin}/shop`,
      og: {
        type: "website",
        title: "Cynos | Premium Oversized T-Shirts & Streetwear India",
        description: "Shop premium oversized T-shirts, anime tees, graphic tees, and streetwear styles for Indian youth at Cynos.",
        url: `${window.location.origin}/shop`,
        site_name: "Cynos",
      },
      jsonLd: {
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        name: "Cynos Streetwear Shop",
        description: "Premium oversized T-shirts, anime tees, graphic tees, and streetwear drops from Cynos.",
        url: `${window.location.origin}/shop`,
      },
      removeJsonLdIds: ["product-json-ld"],
    });
  }, []);

  const updateQuery = (nextValues) => {
    const next = {
      ...(category !== "all" ? { category } : {}),
      ...(color ? { color } : {}),
      ...(search ? { search } : {}),
      ...(sort !== "newest" ? { sort } : {}),
      ...nextValues,
    };
    Object.keys(next).forEach((key) => {
      if (!next[key] || next[key] === "all" || (key === "sort" && next[key] === "newest")) delete next[key];
    });
    setSearchParams(next);
  };

  const setCategory = (nextCategory) => updateQuery({ category: nextCategory });
  const setColorFilter = (nextColor) => updateQuery({ color: nextColor });
  const setSearchFilter = (nextSearch) => updateQuery({ search: nextSearch });
  const setSortFilter = (nextSort) => {
    setSort(nextSort);
    updateQuery({ sort: nextSort });
  };

  const clearFilters = () => {
    setSize("");
    setSortFilter("newest");
    setMaxPrice(2000);
    setSearchParams({});
  };

  const title = category === "all"
    ? "All Drops."
    : `${CATEGORIES.find((c) => c.slug === category)?.label || category}.`;
  const activeFilterCount = [
    category !== "all",
    Boolean(color),
    Boolean(size),
    Boolean(search),
    maxPrice < 2000,
  ].filter(Boolean).length;
  const activeSortLabel = SORT_OPTIONS.find((option) => option.value === sort)?.label || "Newest";

  return (
    <main className="px-4 pb-28 pt-16 md:px-8 md:pt-20 lg:px-12 lg:pb-20" data-testid="shop-page">
      <div className="mb-5 border-b border-white/10 py-6 md:mb-8 md:py-8">
        <span className="font-mono text-[10px] uppercase tracking-[0.26em] text-cynos-red md:text-xs">Collection</span>
        <h1 className="mt-2 font-anton text-[clamp(2rem,8vw,3.2rem)] uppercase leading-none md:text-6xl">{title}</h1>
        <p className="mt-3 max-w-xl text-sm leading-6 text-zinc-400 md:text-base">
          {products.length} products - oversized fits, secure payments, COD, and fast shipping across India.
        </p>
        {error && (
          <div className="mt-4 border border-cynos-red/40 bg-cynos-red/10 p-3 font-mono text-xs uppercase tracking-[0.16em] text-red-200">
            {error}
          </div>
        )}
      </div>

      <CategoryChips category={category} onChange={setCategory} />

      <div className="mb-4 flex items-center justify-between gap-3 lg:hidden">
        <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-zinc-500">
          {activeFilterCount ? `${activeFilterCount} active filters` : "Browse all drops"}
        </span>
        <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-zinc-500">{products.length} drops</span>
      </div>

      <div className="mb-8 hidden min-h-[52px] max-w-xl items-center gap-3 rounded-[8px] border border-white/15 bg-cynos-surface px-4 py-3 lg:flex">
        <Search size={16} className="text-zinc-500" />
        <input
          value={search}
          onChange={(e) => setSearchFilter(e.target.value)}
          placeholder="SEARCH DROPS"
          className="w-full bg-transparent font-mono text-xs uppercase tracking-[0.2em] outline-none placeholder:text-zinc-600"
          data-testid="product-search"
        />
      </div>

      {filtersOpen && (
        <div className="fixed inset-0 z-50 lg:hidden" role="dialog" aria-modal="true" data-testid="mobile-filter-sheet">
          <button
            type="button"
            aria-label="Close filters"
            className="absolute inset-0 bg-black/75"
            onClick={() => setFiltersOpen(false)}
          />
          <div className="absolute inset-y-0 left-0 w-[min(88vw,380px)] overflow-y-auto border-r border-white/10 bg-[#111] p-4 shadow-2xl">
            <div className="mb-4 flex items-center justify-between">
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.26em] text-cynos-red">Refine</div>
                <h2 className="mt-1 font-anton text-3xl uppercase leading-none">Filters.</h2>
              </div>
              <button
                type="button"
                onClick={() => setFiltersOpen(false)}
                className="flex h-10 w-10 items-center justify-center rounded-[8px] border border-white/15 text-white"
                aria-label="Close filters"
              >
                <X size={18} />
              </button>
            </div>
            <FilterSidebar
              size={size}
              onSizeChange={setSize}
              color={color}
              onColorChange={setColorFilter}
              colorOptions={colorOptions}
              maxPrice={maxPrice}
              onMaxPriceChange={setMaxPrice}
              sort={sort}
              onSortChange={setSortFilter}
              onClear={clearFilters}
              search={search}
              onSearchChange={setSearchFilter}
              showSearch
              compact
              onApply={() => setFiltersOpen(false)}
            />
          </div>
        </div>
      )}

      {sortOpen && (
        <div className="fixed inset-0 z-50 lg:hidden" role="dialog" aria-modal="true" aria-labelledby="mobile-sort-title" data-testid="mobile-sort-sheet">
          <button
            type="button"
            aria-label="Close sort options"
            className="absolute inset-0 bg-black/75"
            onClick={() => setSortOpen(false)}
          />
          <div className="absolute inset-x-0 bottom-0 rounded-t-[12px] border border-white/10 bg-[#111] p-4 shadow-2xl">
            <div className="mb-4 flex items-center justify-between">
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.26em] text-cynos-red">Sort By</div>
                <h2 id="mobile-sort-title" className="mt-1 font-anton text-3xl uppercase leading-none">Priority.</h2>
              </div>
              <button
                type="button"
                onClick={() => setSortOpen(false)}
                className="flex h-10 w-10 items-center justify-center rounded-[8px] border border-white/15 text-white"
                aria-label="Close sort options"
              >
                <X size={18} />
              </button>
            </div>
            <div className="grid gap-2">
              {SORT_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => {
                    setSortFilter(option.value);
                    setSortOpen(false);
                  }}
                  className={`min-h-[48px] rounded-[8px] border px-4 text-left font-mono text-xs uppercase tracking-[0.18em] transition ${
                    sort === option.value ? "border-cynos-red bg-cynos-red text-white" : "border-white/15 bg-black/30 text-zinc-300"
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-[280px_1fr] lg:gap-10">
        <FilterSidebar
          size={size}
          onSizeChange={setSize}
          color={color}
          onColorChange={setColorFilter}
          colorOptions={colorOptions}
          maxPrice={maxPrice}
          onMaxPriceChange={setMaxPrice}
          sort={sort}
          onSortChange={setSortFilter}
          onClear={clearFilters}
          className="hidden lg:block"
        />

        <ProductGrid products={products} loading={loading} />
      </div>

      <div className="fixed inset-x-0 bottom-0 z-30 border-t border-white/10 bg-black/95 p-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] backdrop-blur lg:hidden">
        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={() => setFiltersOpen(true)}
            className="inline-flex min-h-[48px] items-center justify-center gap-2 rounded-[8px] border border-white/15 bg-cynos-surface px-4 font-mono text-[11px] uppercase tracking-[0.18em] text-white"
            data-testid="mobile-filter-open"
          >
            <SlidersHorizontal size={15} />
            Filter {activeFilterCount ? `(${activeFilterCount})` : ""}
          </button>
          <button
            type="button"
            onClick={() => setSortOpen(true)}
            aria-label={`Sort products, current sort ${activeSortLabel}`}
            className="inline-flex min-h-[48px] items-center justify-center gap-2 rounded-[8px] border border-white/15 bg-cynos-surface px-4 font-mono text-[11px] uppercase tracking-[0.18em] text-white"
            data-testid="mobile-sort-open"
          >
            <ArrowUpDown size={15} />
            Sort
          </button>
        </div>
      </div>
    </main>
  );
};

export default ProductListingPage;
